import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Calculator, FileText } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import BasicParametersSection from './BasicParametersSection';
import AdditionalParametersSection from './AdditionalParametersSection';
import TradingStrategySection from './TradingStrategySection';
import TrancheInvestmentSection from './TrancheInvestmentSection';
import ScenarioAnalysisSection from './ScenarioAnalysisSection';
import TaxationSection from './TaxationSection';

/**
 * Компонент основной формы расчета инвестиций в криптовалюту
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  investmentData,
  setInvestmentData,
  cryptocurrencyTypes,
  investmentCurrencies,
  investmentFrequencies,
  tradingStrategies,
  saveInvestment 
}) => {
  
  // Состояние для имени сохраняемой инвестиции
  const [saveInvestmentName, setSaveInvestmentName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновляем процентную ставку автоматически при изменении типа криптовалюты
      if (name === 'cryptocurrencyType') {
        const selectedCryptoType = cryptocurrencyTypes.find(type => type.id === fieldValue);
        if (selectedCryptoType) {
          const currentRate = parseFloatOrDefault(prev.expectedGrowthRate, 0);
          const isRateDefault = cryptocurrencyTypes.some(lt => currentRate === lt.defaultMonthlyGrowth);
          if (isRateDefault || !prev.expectedGrowthRate || currentRate <= 0) {
            newState.expectedGrowthRate = selectedCryptoType.defaultMonthlyGrowth.toString();
          }
        }
      }

      // Сбрасываем связанные поля если функция отключена
      if (name === 'additionalInvestments' && !checked) {
        newState.additionalInvestmentAmount = '10000';
        newState.additionalInvestmentFrequency = 'monthly';
      }
      
      if (name === 'includeTradingStrategy' && !checked) {
        newState.tradingStrategy = 'hodl';
      }

      if (name === 'hasTranchesInvestment' && !checked) {
        newState.tranchesCount = '3';
        newState.tranchesFrequency = 'monthly';
      }

      if (name === 'scenarioAnalysis' && !checked) {
        newState.pessimisticScenario = '-10';
        newState.optimisticScenario = '30';
      }

      if (name === 'reinvestProfit' && !checked) {
        newState.reinvestmentPercentage = '50';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [cryptocurrencyTypes, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      initialInvestment, investmentPeriod, expectedGrowthRate, cryptocurrencyType,
      reinvestProfit, reinvestmentPercentage, feePercentage, taxRate,
      additionalInvestments, additionalInvestmentAmount, additionalInvestmentFrequency,
      hasTranchesInvestment, tranchesCount, tranchesFrequency,
      scenarioAnalysis, pessimisticScenario, optimisticScenario
    } = formData;

    // Парсинг числовых значений
    const numInitialInvestment = parseFloatOrDefault(initialInvestment, -1);
    const numInvestmentPeriod = parseIntOrDefault(investmentPeriod, -1);
    const numExpectedGrowthRate = parseFloatOrDefault(expectedGrowthRate, -1);
    const numFeePercentage = parseFloatOrDefault(feePercentage, -1);
    const numTaxRate = parseFloatOrDefault(taxRate, -1);
    const numReinvestmentPercentage = parseFloatOrDefault(reinvestmentPercentage, -1);

    // Валидация базовых полей
    if (numInitialInvestment <= 0) newErrors.initialInvestment = 'Сумма должна быть положительной.';
    if (!newErrors.initialInvestment && numInitialInvestment > 100000000) newErrors.initialInvestment = 'Максимальная инвестиция 100 млн.';
    
    if (numInvestmentPeriod <= 0) newErrors.investmentPeriod = 'Срок должен быть положительным.';
    if (!newErrors.investmentPeriod && numInvestmentPeriod > 120) newErrors.investmentPeriod = 'Максимальный срок 120 месяцев (10 лет).';
    
    if (numExpectedGrowthRate <= -100) newErrors.expectedGrowthRate = 'Ставка должна быть больше -100%.';
    if (!newErrors.expectedGrowthRate && numExpectedGrowthRate > 1000) newErrors.expectedGrowthRate = 'Ставка не может быть более 1000%.';

    if (!cryptocurrencyType) newErrors.cryptocurrencyType = 'Выберите криптовалюту.';

    // Валидация дополнительных параметров
    if (reinvestProfit) {
      if (numReinvestmentPercentage < 0) newErrors.reinvestmentPercentage = 'Процент реинвестирования не может быть отрицательным.';
      if (!newErrors.reinvestmentPercentage && numReinvestmentPercentage > 100) newErrors.reinvestmentPercentage = 'Процент не может быть больше 100%.';
    }

    if (numFeePercentage < 0) newErrors.feePercentage = 'Комиссия не может быть отрицательной.';
    if (!newErrors.feePercentage && numFeePercentage > 100) newErrors.feePercentage = 'Комиссия не может быть больше 100%.';

    if (numTaxRate < 0) newErrors.taxRate = 'Налоговая ставка не может быть отрицательной.';
    if (!newErrors.taxRate && numTaxRate > 100) newErrors.taxRate = 'Налоговая ставка не может быть больше 100%.';

    // Валидация дополнительных инвестиций
    if (additionalInvestments) {
      const numAdditionalInvestmentAmount = parseFloatOrDefault(additionalInvestmentAmount, -1);
      if (numAdditionalInvestmentAmount <= 0) newErrors.additionalInvestmentAmount = 'Сумма должна быть положительной.';
      if (!additionalInvestmentFrequency) newErrors.additionalInvestmentFrequency = 'Выберите периодичность.';
    }

    // Валидация транша
    if (hasTranchesInvestment) {
      const numTranchesCount = parseIntOrDefault(tranchesCount, -1);
      if (numTranchesCount <= 0) newErrors.tranchesCount = 'Количество траншей должно быть положительным.';
      if (!newErrors.tranchesCount && numTranchesCount > 100) newErrors.tranchesCount = 'Максимум 100 траншей.';
      if (!tranchesFrequency) newErrors.tranchesFrequency = 'Выберите периодичность.';
    }

    // Валидация сценарного анализа
    if (scenarioAnalysis) {
      const numPessimisticScenario = parseFloatOrDefault(pessimisticScenario, 0);
      const numOptimisticScenario = parseFloatOrDefault(optimisticScenario, 0);

      if (numPessimisticScenario < -100) newErrors.pessimisticScenario = 'Минимальное значение -100%.';
      if (numOptimisticScenario < numPessimisticScenario) {
        newErrors.optimisticScenario = 'Оптимистичный сценарий должен быть больше пессимистичного.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета инвестиций
  const calculateInvestment = useCallback(() => {
    if (!validateForm()) {
      // Прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setInvestmentData([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const initialInvestmentAmount = parseFloatOrDefault(formData.initialInvestment);
        if (initialInvestmentAmount <= 0) {
          setErrors({ general: "Сумма инвестиции должна быть положительной." });
          setIsCalculating(false);
          return;
        }

        const investmentPeriodMonths = parseIntOrDefault(formData.investmentPeriod);
        const monthlyGrowthRate = parseFloatOrDefault(formData.expectedGrowthRate) / 100;
        const feePercentage = parseFloatOrDefault(formData.feePercentage) / 100;
        const taxRate = parseFloatOrDefault(formData.taxRate) / 100;
        
        const reinvestProfit = formData.reinvestProfit;
        const reinvestmentPercentage = reinvestProfit ? parseFloatOrDefault(formData.reinvestmentPercentage) / 100 : 0;
        
        const hasAdditionalInvestments = formData.additionalInvestments;
        const additionalInvestmentAmount = hasAdditionalInvestments ? parseFloatOrDefault(formData.additionalInvestmentAmount) : 0;
        const additionalInvestmentFrequency = formData.additionalInvestmentFrequency;
        
        const hasTranchesInvestment = formData.hasTranchesInvestment;
        const tranchesCount = hasTranchesInvestment ? parseIntOrDefault(formData.tranchesCount) : 1;
        const tranchesFrequency = formData.tranchesFrequency;
        
        const includeTradingStrategy = formData.includeTradingStrategy;
        const tradingStrategy = formData.tradingStrategy;
        
        const scenarioAnalysis = formData.scenarioAnalysis;
        const pessimisticScenario = scenarioAnalysis ? parseFloatOrDefault(formData.pessimisticScenario) / 100 : 0;
        const optimisticScenario = scenarioAnalysis ? parseFloatOrDefault(formData.optimisticScenario) / 100 : 0;

        // --- Определение частоты дополнительных инвестиций ---
        let additionalInvestmentFrequencyMonths = 0;
        if (hasAdditionalInvestments && additionalInvestmentAmount > 0) {
          switch (additionalInvestmentFrequency) {
            case 'monthly': additionalInvestmentFrequencyMonths = 1; break;
            case 'quarterly': additionalInvestmentFrequencyMonths = 3; break;
            case 'semi_annually': additionalInvestmentFrequencyMonths = 6; break;
            case 'annually': additionalInvestmentFrequencyMonths = 12; break;
            default: additionalInvestmentFrequencyMonths = 0;
          }
        }

        // --- Определение частоты траншей ---
        let tranchesFrequencyMonths = 0;
        if (hasTranchesInvestment) {
          switch (tranchesFrequency) {
            case 'monthly': tranchesFrequencyMonths = 1; break;
            case 'quarterly': tranchesFrequencyMonths = 3; break;
            case 'semi_annually': tranchesFrequencyMonths = 6; break;
            case 'annually': tranchesFrequencyMonths = 12; break;
            default: tranchesFrequencyMonths = 0;
          }
        }
        
        // --- Коэффициент стратегии ---
        let strategyMultiplier = 1.0;
        if (includeTradingStrategy) {
          // Разные стратегии имеют разный потенциал доходности
          switch (tradingStrategy) {
            case 'hodl': strategyMultiplier = 1.0; break; // Базовая стратегия
            case 'dca': strategyMultiplier = 1.05; break; // Немного лучше из-за усреднения
            case 'active': strategyMultiplier = 1.15; break; // Потенциально выше, но с большими рисками
            case 'grid': strategyMultiplier = 1.1; break;
            case 'iceberg': strategyMultiplier = 1.08; break;
            default: strategyMultiplier = 1.0;
          }
        }

        // --- Расчет инвестиций по месяцам ---
        const initialTranchAmount = hasTranchesInvestment ? initialInvestmentAmount / tranchesCount : initialInvestmentAmount;
        let currentInvestment = hasTranchesInvestment ? initialTranchAmount : initialInvestmentAmount;
        let totalInvestment = currentInvestment;
        let totalProfit = 0;
        let totalFees = 0;
        let totalTax = 0;
        let tranchesRemaining = hasTranchesInvestment ? tranchesCount - 1 : 0;
        
        // Создаем массив данных по месяцам
        const monthlyData = [];
        const baselineData = [];
        const pessimisticData = [];
        const optimisticData = [];
        
        for (let month = 1; month <= investmentPeriodMonths; month++) {
          // Расчет прибыли за месяц (с учетом стратегии)
          const monthlyProfit = currentInvestment * monthlyGrowthRate * strategyMultiplier;
          
          // Комиссия биржи/площадки
          const monthlyFee = (Math.abs(monthlyProfit) + (hasAdditionalInvestments && month % additionalInvestmentFrequencyMonths === 0 ? additionalInvestmentAmount : 0)) * feePercentage;
          
          // Чистая прибыль после комиссии
          const netProfit = monthlyProfit - monthlyFee;
          
          // Налог (только с положительной прибыли)
          const monthlyTax = netProfit > 0 ? netProfit * taxRate : 0;
          
          // Прибыль после налогов
          const profitAfterTax = netProfit - monthlyTax;
          
          // Реинвестирование
          const reinvestedAmount = reinvestProfit && profitAfterTax > 0 ? profitAfterTax * reinvestmentPercentage : 0;
          
          // Дополнительная инвестиция (если есть)
          const additionalInvestment = hasAdditionalInvestments && month % additionalInvestmentFrequencyMonths === 0 ? additionalInvestmentAmount : 0;
          
          // Транш (если есть)
          const tranchInvestment = hasTranchesInvestment && tranchesRemaining > 0 && month % tranchesFrequencyMonths === 0 ? initialTranchAmount : 0;
          if (tranchInvestment > 0) {
            tranchesRemaining--;
          }
          
          // Обновление текущей инвестиции
          currentInvestment += profitAfterTax + additionalInvestment + tranchInvestment - reinvestedAmount;
          
          // Обновление сумм
          totalInvestment += additionalInvestment + tranchInvestment;
          totalProfit += profitAfterTax;
          totalFees += monthlyFee;
          totalTax += monthlyTax;
          
          // Запись данных месяца
          monthlyData.push({
            month,
            investment: currentInvestment,
            profit: profitAfterTax,
            fees: monthlyFee,
            tax: monthlyTax,
            reinvested: reinvestedAmount,
            additional: additionalInvestment,
            tranche: tranchInvestment,
            cumulative: {
              investment: totalInvestment,
              profit: totalProfit,
              fees: totalFees,
              tax: totalTax
            }
          });
          
          // Расчет для базового сценария (без учета стратегии)
          const baselineInvestment = initialInvestmentAmount * Math.pow(1 + monthlyGrowthRate, month);
          baselineData.push({
            month,
            investment: baselineInvestment
          });
          
          // Расчет для пессимистичного сценария
          if (scenarioAnalysis) {
            const pessimisticInvestment = initialInvestmentAmount * Math.pow(1 + pessimisticScenario / 12, month);
            pessimisticData.push({
              month,
              investment: pessimisticInvestment
            });
            
            // Расчет для оптимистичного сценария
            const optimisticInvestment = initialInvestmentAmount * Math.pow(1 + optimisticScenario / 12, month);
            optimisticData.push({
              month,
              investment: optimisticInvestment
            });
          }
        }

        // --- Расчет итоговых показателей ---
        const finalInvestment = monthlyData[monthlyData.length - 1].investment;
        const totalReturn = finalInvestment - initialInvestmentAmount;
        const returnPercentage = (totalReturn / initialInvestmentAmount) * 100;
        const annualizedReturn = Math.pow(finalInvestment / initialInvestmentAmount, 12 / investmentPeriodMonths) - 1;
        const annualizedReturnPercentage = annualizedReturn * 100;
        
        // Расчет риска (на основе волатильности и выбранной стратегии)
        const selectedCrypto = cryptocurrencyTypes.find(c => c.id === formData.cryptocurrencyType);
        const selectedStrategy = tradingStrategies.find(s => s.id === tradingStrategy);
        const baseRiskScore = selectedCrypto ? selectedCrypto.riskScore : 70;
        const strategyRiskScore = selectedStrategy && includeTradingStrategy ? selectedStrategy.riskScore : 65;
        
        // Комбинированный риск с учетом и криптовалюты, и стратегии
        const riskScore = (baseRiskScore * 0.7) + (strategyRiskScore * 0.3);
        
        // Сопоставление риска и доходности
        const riskAdjustedReturn = annualizedReturnPercentage / (riskScore / 50);
        
        // Финальный результат
        const finalResults = {
          initialInvestment: initialInvestmentAmount,
          finalInvestment,
          totalInvestment,
          totalProfit,
          totalReturn,
          returnPercentage,
          annualizedReturnPercentage,
          totalFees,
          totalTax,
          investmentPeriodMonths,
          cryptoType: formData.cryptocurrencyType,
          currencyType: formData.investmentCurrency,
          riskScore,
          riskAdjustedReturn,
          monthlyGrowthRate: monthlyGrowthRate * 100, // В процентах
          reinvestmentPercentage: reinvestmentPercentage * 100, // В процентах
          hasScenariosAnalysis: scenarioAnalysis,
          pessimisticFinalValue: scenarioAnalysis ? pessimisticData[pessimisticData.length - 1].investment : 0,
          optimisticFinalValue: scenarioAnalysis ? optimisticData[optimisticData.length - 1].investment : 0,
          strategy: includeTradingStrategy ? tradingStrategy : 'none'
        };
        
        // Обновление результатов и данных
        setResults(finalResults);
        setInvestmentData({
          monthly: monthlyData,
          baseline: baselineData,
          pessimistic: pessimisticData,
          optimistic: optimisticData
        });

      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setInvestmentData([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setInvestmentData, setIsCalculating, cryptocurrencyTypes, tradingStrategies]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      initialInvestment: '100000',
      cryptocurrencyType: 'bitcoin',
      investmentCurrency: 'rub',
      investmentPeriod: '12',
      expectedGrowthRate: '15',
      reinvestProfit: false,
      reinvestmentPercentage: '50',
      feePercentage: '1.5',
      riskLevel: 'medium',
      taxRate: '13',
      additionalInvestments: false,
      additionalInvestmentAmount: '10000',
      additionalInvestmentFrequency: 'monthly',
      includeTradingStrategy: false,
      tradingStrategy: 'hodl',
      hasTranchesInvestment: false,
      tranchesCount: '3',
      tranchesFrequency: 'monthly',
      scenarioAnalysis: false,
      pessimisticScenario: '-10',
      optimisticScenario: '30',
    });
    setResults(null);
    setInvestmentData([]);
    setErrors({});
    setSaveInvestmentName('');
    setShowSaveDialog(false);
  };

  // Экспорт данных в CSV
  const exportToCSV = () => {
    if (!results || !investmentData || !investmentData.monthly || investmentData.monthly.length === 0) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки на основе расчетов
    const headers = ['Месяц', 'Стоимость инвестиции', 'Прибыль за месяц', 'Комиссии', 'Налоги', 
                    'Реинвестировано', 'Дополнительные вложения', 'Транш', 'Общие инвестиции', 'Общая прибыль'];

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    const currencySymbol = investmentCurrencies.find(c => c.id === formData.investmentCurrency)?.symbol || '₽';
    
    const rows = investmentData.monthly.map(item => {
      return [
        item.month,
        formatCsvNumber(item.investment),
        formatCsvNumber(item.profit),
        formatCsvNumber(item.fees),
        formatCsvNumber(item.tax),
        formatCsvNumber(item.reinvested),
        formatCsvNumber(item.additional),
        formatCsvNumber(item.tranche),
        formatCsvNumber(item.cumulative.investment),
        formatCsvNumber(item.cumulative.profit)
      ].join(';');
    });

    // Добавим итоговую строку
    const totalRow = [
      '', 'ИТОГО:', 
      formatCsvNumber(results.totalProfit),
      formatCsvNumber(results.totalFees),
      formatCsvNumber(results.totalTax),
      formatCsvNumber(0), // Реинвестировано (данных нет)
      formatCsvNumber(results.totalInvestment - results.initialInvestment), // Дополнительные вложения
      '', // Транш (данных нет)
      formatCsvNumber(results.totalInvestment),
      formatCsvNumber(results.totalProfit)
    ].join(';');
    
    // Добавим основную информацию в начало файла
    const cryptoName = cryptocurrencyTypes.find(c => c.id === formData.cryptocurrencyType)?.name || formData.cryptocurrencyType;
    const infoRows = [
      [`Первоначальная инвестиция:`, `${formatCsvNumber(results.initialInvestment)} ${currencySymbol}`],
      [`Криптовалюта:`, cryptoName],
      [`Срок инвестирования:`, `${results.investmentPeriodMonths} мес.`],
      [`Ежемесячный рост:`, `${formatCsvNumber(results.monthlyGrowthRate)}%`],
      [`Итоговая стоимость:`, `${formatCsvNumber(results.finalInvestment)} ${currencySymbol}`],
      [`Общая доходность:`, `${formatCsvNumber(results.returnPercentage)}%`],
      [`Годовая доходность:`, `${formatCsvNumber(results.annualizedReturnPercentage)}%`],
      [`Уровень риска:`, `${formatCsvNumber(results.riskScore)}/100`],
      [`Дата расчета:`, new Date().toLocaleDateString('ru-RU')],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows, totalRow].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `инвестиция_в_${formData.cryptocurrencyType}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Экспорт отчета в PDF
  const exportToPDF = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }
    
    alert("Функция экспорта в PDF будет доступна в следующем обновлении.");
    // В реальном приложении здесь был бы код для генерации PDF
  };

  // Обработчик сохранения инвестиции
  const handleSaveInvestment = () => {
    if (showSaveDialog) {
      const savedResult = saveInvestment(saveInvestmentName || null);
      if (savedResult) {
        alert(`Инвестиция "${savedResult.name}" успешно сохранена!`);
      }
      setShowSaveDialog(false);
      setSaveInvestmentName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateInvestment();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart2 className="w-6 h-6 text-indigo-500" />
          Параметры криптовалютной инвестиции
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры инвестиции */}
        <BasicParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          cryptocurrencyTypes={cryptocurrencyTypes}
          investmentCurrencies={investmentCurrencies}
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          investmentFrequencies={investmentFrequencies}
        />

        {/* Торговая стратегия */}
        <TradingStrategySection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          tradingStrategies={tradingStrategies}
        />

        {/* Трейдинг траншами */}
        <TrancheInvestmentSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          investmentFrequencies={investmentFrequencies}
        />

        {/* Анализ сценариев */}
        <ScenarioAnalysisSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
        />

        {/* Налогообложение */}
        <TaxationSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
        />

        {/* Диалог сохранения инвестиции */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveInvestmentName}
                onChange={(e) => setSaveInvestmentName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveInvestment}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Выполняем расчет...' : 'Рассчитать инвестицию'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <>
              <Button
                type="button"
                onClick={exportToCSV}
                icon={<Download className="w-5 h-5" />}
                variant="secondary"
                disabled={isCalculating}
                size="lg"
              >
                Скачать CSV
              </Button>
              <Button
                type="button"
                onClick={exportToPDF}
                icon={<FileText className="w-5 h-5" />}
                variant="secondary"
                disabled={isCalculating}
                size="lg"
              >
                Экспорт PDF
              </Button>
            </>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveInvestment}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;